import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { MessageService, SharedModule } from 'primeng/api';
import { ActivatedRoute } from '@angular/router';
import { map, startWith, switchMap } from 'rxjs/operators';
import { combineLatest, Subscription } from 'rxjs';
import { AsyncPipe, NgIf } from '@angular/common';
import { Merchant, Representative } from '@shared/models';
import { MerchantOverviewFormComponent } from '@app/areas/merchants/forms/merchant-overview-form/merchant-overview-form.component';
import { MerchantVerticalsCardComponent } from '@app/areas/merchants/components/merchant-verticals-card/merchant-verticals-card.component';
import { MerchantOverviewFormService } from '@app/areas/merchants/services/merchant-overview-form.service';
import { MerchantsApiService } from '@app/areas/merchants/services/merchants.api.service';
import { RepresentativesApiService } from '@app/areas/admin/representatives/services/representatives.api.service';

@Component({
  selector: 'app-merchant-overview',
  templateUrl: './merchant-overview.component.html',
  styleUrls: ['./merchant-overview.component.scss'],
  standalone: true,
  imports: [
    MerchantOverviewFormComponent,
    MerchantVerticalsCardComponent,
    ButtonModule,
    CardModule,
    SharedModule,
    AsyncPipe,
    NgIf,
  ],
})
export class MerchantOverviewComponent implements OnInit, OnDestroy {
  private readonly merchantOverviewFormService = inject(MerchantOverviewFormService);
  private readonly merchantsApiService = inject(MerchantsApiService);
  private readonly representativesApiService = inject(RepresentativesApiService);
  private readonly messageService = inject(MessageService);
  private readonly route = inject(ActivatedRoute);

  protected merchant: Merchant | null = null;
  protected notFound = false;
  protected isSaving = false;
  protected representatives: Representative[] = [];

  protected mode$ = this.merchantOverviewFormService.mode$;
  protected isSaveDisabled$ = this.merchantOverviewFormService.getForm().statusChanges.pipe(
    startWith(this.merchantOverviewFormService.getForm().status),
    map((status) => status !== 'VALID')
  );

  private merchantId = 0;
  private subs = new Subscription();

  ngOnInit(): void {
    this.subs.add(
      this.route
        .parent!.paramMap.pipe(
          map((params) => ({
            coalitionId: Number(params.get('coalitionId')),
            merchantId: Number(params.get('merchantId')),
          })),
          switchMap(({ coalitionId, merchantId }) => {
            this.merchantId = merchantId;
            return combineLatest([
              this.merchantsApiService.getById(merchantId),
              this.representativesApiService.getByCoalitionId(coalitionId),
            ]);
          })
        )
        .subscribe(([merchant, representatives]) => {
          this.representatives = representatives;

          if (!merchant) {
            this.notFound = true;
            return;
          }

          this.notFound = false;
          this.merchant = merchant;
          this.merchantOverviewFormService.initializeForView(merchant);
        })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected edit(): void {
    if (this.merchant) {
      this.merchantOverviewFormService.initializeForEdit(this.merchant);
    }
  }

  protected cancelEdit(): void {
    if (this.merchant) {
      this.merchantOverviewFormService.initializeForView(this.merchant);
    }
  }

  protected saveMerchant(): void {
    const form = this.merchantOverviewFormService.getForm();
    if (!form.valid || !this.merchant) {
      return;
    }

    this.isSaving = true;
    const formValue = form.getRawValue();

    this.subs.add(
      this.merchantsApiService
        .update(this.merchantId, {
          Name: formValue.Name!,
          BasePrice: formValue.BasePrice!,
          PerSmsprice: formValue.PerSmsprice!,
          SalesRepresentativeId: formValue.SalesRepresentativeId,
        })
        .subscribe((merchant) => {
          this.isSaving = false;
          if (merchant) {
            this.merchant = merchant;
            this.merchantOverviewFormService.initializeForView(merchant);
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'ERROR: Update Merchant',
              detail: 'Something went wrong while updating the merchant',
            });
          }
        })
    );
  }
}
